import { portalText } from "../../i18n/portal.js";
/**
 * The purchase terms, offered where credit is bought (plan-014 review, API-UI C-3; owner decision PD-8).
 *
 * Every purchase — a top-up included — is refused `409 terms_acceptance_required` until the organization accepted the
 * purchase terms revision in force (`packages/payments` runner). Organizations from before plan-014 and every
 * operator-created organization have no acceptance (PD-8: no backfill), and the only accept control used to sit
 * beside a plan quote — so an Owner who opened Add credit completed the card form and was refused, with nowhere to
 * accept. This gate is read BEFORE the processor's form opens: accepted already — nothing to say; not yet, and this
 * person may accept (an Owner or a Finance member) — a box to tick, accepted as part of "Continue to card details";
 * not yet, and this person may not — who must, and the button waits. A gateway without the terms read (the sandbox,
 * which takes no real payment) asks nothing.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";

import { apiProblemText } from "../../api/api-error.js";
import { useGateway } from "../../api/provider.js";
import { useOpenAccount } from "../account-admin/overview/account-overview-screen.js";
import "../finance/plans/purchase-terms-gateway.js";
import type { PurchaseTermsView } from "../finance/plans/purchase-terms-gateway.js";

export interface PurchaseTermsGate {
  /** True while buying would be refused: the terms are loading, unreadable, or not accepted and not ticked. */
  readonly blocking: boolean;
  /** What the dialog shows about the terms (nothing once they are accepted). */
  readonly line: ReactNode;
  /** Accepts the ticked revision for the organization when it is not yet accepted; nothing otherwise. */
  readonly acceptIfNeeded: () => Promise<void>;
}

/** The query key the Plans page reads too, so an acceptance in one place is seen in the other. */
export function purchaseTermsKey(accountId: string | null): readonly [string, string] {
  return ["purchase-terms", accountId ?? "none"];
}

export function usePurchaseTermsGate(active: boolean): PurchaseTermsGate {
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const { accountId } = useOpenAccount();
  const readTerms = gateway.readPurchaseTerms;
  const acceptTerms = gateway.acceptPurchaseTerms;
  const [ticked, setTicked] = useState(false);
  const terms = useQuery({
    queryKey: purchaseTermsKey(accountId),
    queryFn: (): Promise<PurchaseTermsView> => {
      if (readTerms === undefined || accountId === null) throw new Error("unavailable");
      return readTerms(accountId);
    },
    enabled: active && readTerms !== undefined && accountId !== null,
    staleTime: 0,
  });

  if (readTerms === undefined) {
    return { blocking: false, line: null, acceptIfNeeded: () => Promise.resolve() };
  }
  if (terms.isError) {
    return {
      blocking: true,
      line: <p className="c-note c-note-critical">{apiProblemText(terms.error, portalText("finance", "m0303"))}</p>,
      acceptIfNeeded: () => Promise.resolve(),
    };
  }
  const current = terms.data;
  if (current === undefined) {
    return { blocking: true, line: <p className="c-note">{portalText("finance", "m0304")}</p>, acceptIfNeeded: () => Promise.resolve() };
  }
  if (current.accepted) return { blocking: false, line: null, acceptIfNeeded: () => Promise.resolve() };

  const documents = (
    <>
      <a href={current.termsUrl} target="_blank" rel="noreferrer">{portalText("finance", "m0305")}</a>{" "}{portalText("finance", "m0306")}{" "}
      <a href={current.privacyUrl} target="_blank" rel="noreferrer">{portalText("finance", "m0307")}</a>{" "}
      {portalText("finance", "m0308", { value1: current.revision })}
    </>
  );
  if (!current.canAccept || acceptTerms === undefined) {
    return {
      blocking: true,
      line: (
        <p className="c-note" role="status">{portalText("finance", "m0309")}{documents}{portalText("finance", "m0310")}</p>
      ),
      acceptIfNeeded: () => Promise.resolve(),
    };
  }
  return {
    blocking: !ticked,
    line: (
      <label className="bl-terms">
        <input
          type="checkbox"
          checked={ticked}
          onChange={(event) => {
            setTicked(event.currentTarget.checked);
          }}
        />
        <span>{portalText("finance", "m0311")}{documents}{portalText("finance", "m0312")}</span>
      </label>
    ),
    acceptIfNeeded: async () => {
      if (!ticked || accountId === null) throw new Error(portalText("finance", "m0313"));
      await acceptTerms(accountId, current.revision);
      await queryClient.invalidateQueries({ queryKey: purchaseTermsKey(accountId) });
    },
  };
}
